<script setup lang="ts">
import { computed, ref } from "vue";

import { switchSessionOrgTreeLevelSelected } from "@/analytics/events/other";
import { trackEvent } from "@/analytics/track";
import OrganizationBaseTree from "@/components/OrganizationBaseTree/OrganizationBaseTree.vue";
import type {
  OrganizationTreeNodeData,
  SelectedNode,
} from "@/components/OrganizationBaseTree/types";
import TeamOverview from "@/components/TeamOverview/TeamOverview.vue";
import DropdownMenu from "@/components/ui/DropdownMenu/DropdownMenu.vue";
import IconButton from "@/components/ui/IconButton/IconButton.vue";
import { isArtBoard, isFlexBoard, isSolutionBoard, isTeamBoard } from "@/model/board";
import { goToBoard } from "@/router/navigation";
import { useBoardStore } from "@/store/board";
import { useTeamStore } from "@/store/team";
import { eventHandlerElem } from "@/utils/dom/dom";

const selected = computed<SelectedNode>(() => {
  const currentBoard = useBoardStore().currentBoard();
  const { type } = currentBoard;

  //typescript is not clever enough for isTeamBoard(type)
  if (type === "team") {
    return { level: "team", id: currentBoard.team.id };
  }
  if (isArtBoard(type)) {
    return { level: "art", id: currentBoard.artId ?? "" };
  }
  if (isSolutionBoard(type)) {
    return { level: "solution-train" };
  }
  return { level: "none" };
});

const handleSelect = (node: OrganizationTreeNodeData) => {
  const currentBoard = useBoardStore().currentBoard();
  const { type } = currentBoard;

  const goToTeam = (id: string) => {
    trackEvent(switchSessionOrgTreeLevelSelected("team"));
    const team = useTeamStore().findTeam({ id });
    void goToBoard({ type: "team", team });
  };

  const goToArt = (artId: string) => {
    trackEvent(switchSessionOrgTreeLevelSelected("art"));
    if (isTeamBoard(type) || isFlexBoard(type) || type === "solution") {
      void goToBoard({ type: "program", artId });
    } else if (type === "solution_backlog") {
      void goToBoard({ type: "backlog", artId });
    } else {
      void goToBoard({ type, artId });
    }
  };

  const goToSolutionTrain = () => {
    trackEvent(switchSessionOrgTreeLevelSelected("solution-train"));
    if (!isSolutionBoard(type)) {
      void goToBoard({ type: type === "backlog" ? "solution_backlog" : "solution" });
    }
  };

  switch (node.levelContext.level) {
    case "team":
      goToTeam(node.levelContext.id);
      break;

    case "art":
      goToArt(node.levelContext.id);
      break;

    case "solution-train":
      goToSolutionTrain();
      break;
  }
};

const findTeam = (id: string) => useTeamStore().findTeam({ id })!;

const POPOVER_GAP = 8;
const popoverOffset = ref({ top: POPOVER_GAP });

// The popover starts at the panel's edge instead of the trigger's, so the panel stays visible
function alignPopoverToPanel(event: MouseEvent) {
  const trigger = eventHandlerElem(event);
  const panel = trigger.closest(".side-panel")!;
  const distance = panel.getBoundingClientRect().right - trigger.getBoundingClientRect().right;
  popoverOffset.value = { top: distance + POPOVER_GAP };
}
</script>

<template>
  <OrganizationBaseTree :selected @select="handleSelect">
    <template #actions="{ node }">
      <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events vuejs-accessibility/no-static-element-interactions -->
      <span v-if="node.levelContext.level === 'team'" @click.stop @keydown.stop>
        <DropdownMenu
          placement="right-start"
          :offset="popoverOffset"
          no-flip
          width="xl"
          max-height="xl"
          stay-open-on-content-click
        >
          <template #trigger="{ isOpen, contentId }">
            <IconButton
              data-dropdown-trigger
              icon="general/open-dialog"
              size="x-small"
              flat
              :inverse="node.selected"
              :text="$t('switchSession.showTeam')"
              :activated="isOpen"
              :aria-controls="contentId"
              aria-haspopup="dialog"
              @click="alignPopoverToPanel"
            />
          </template>
          <TeamOverview :team="findTeam(node.levelContext.id)" />
        </DropdownMenu>
      </span>
    </template>
  </OrganizationBaseTree>
</template>
